import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { wsService } from './services/websocket';
import ErrorBoundary from './components/ErrorBoundary';
import AlertProvider from './components/AlertProvider';
import LoginScreen from './screens/auth/LoginScreen';
import RegisterDriverScreen from './screens/auth/RegisterDriverScreen';
import VerifyPhoneScreen from './screens/auth/VerifyPhoneScreen';
import VerifyEmailScreen from './screens/auth/VerifyEmailScreen';
import DriverHomeScreen from './screens/driver/DriverHomeScreen';
import ActiveRideScreen from './screens/driver/ActiveRideScreen';
import RideDetailsScreen from './screens/driver/RideDetailsScreen';
import EarningsScreen from './screens/driver/EarningsScreen';
import DocumentsScreen from './screens/driver/DocumentsScreen';
import VehiclesScreen from './screens/driver/VehiclesScreen';
import DriverProfileScreen from './screens/driver/DriverProfileScreen';
import ChatScreen from './screens/common/ChatScreen';
import RatingsScreen from './screens/common/RatingsScreen';
import SOSScreen from './screens/common/SOSScreen';
import './App.css';

const App: React.FC = () => {
  const { isAuthenticated, user, isLoading, loadUser } = useAuthStore();

  useEffect(() => {
    loadUser();
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      wsService.disconnect();
      return;
    }

    const initializeConnection = async () => {
      const currentNamespace = wsService.getNamespace();
      const isConnected = wsService.isConnected();

      if (isConnected && currentNamespace === 'driver') {
        return;
      }

      try {
        await wsService.ensureConnection('driver');
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('Failed to connect WebSocket for driver:', error);
        }
      }
    };

    initializeConnection();
  }, [isAuthenticated, user]);

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p className="loading-text">Loading...</p>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <AlertProvider>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <Routes>
            {!isAuthenticated ? (
              <>
                <Route path="/login" element={<LoginScreen />} />
                <Route path="/register-driver" element={<RegisterDriverScreen />} />
                <Route path="/verify-phone" element={<VerifyPhoneScreen />} />
                <Route path="/verify-email" element={<VerifyEmailScreen />} />
                <Route path="*" element={<Navigate to="/login" replace />} />
              </>
            ) : (
              <>
                <Route path="/" element={<DriverHomeScreen />} />
                <Route path="/active-ride" element={<ActiveRideScreen />} />
                <Route path="/ride/:rideId" element={<RideDetailsScreen />} />
                <Route path="/earnings" element={<EarningsScreen />} />
                <Route path="/documents" element={<DocumentsScreen />} />
                <Route path="/vehicles" element={<VehiclesScreen />} />
                <Route path="/profile" element={<DriverProfileScreen />} />
                <Route path="/chat/:rideId" element={<ChatScreen />} />
                <Route path="/ratings" element={<RatingsScreen />} />
                <Route path="/sos" element={<SOSScreen />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </>
            )}
          </Routes>
        </BrowserRouter>
      </AlertProvider>
    </ErrorBoundary>
  );
};

export default App;

